import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	attachClassifiersToTag,
	createTag,
	deleteTag,
	detachClassifiersFromTag,
	listTags,
	updateTag,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/services/tags.service';
import {
	ClassifierAssignmentDto,
	CreateTagDto,
	UpdateTagDto,
} from '@dealstack/shared/lib/generated';

const QUERY_KEY = 'tags';

export const useTagsList = ({
	mipId,
	isArchived,
	enabled = true,
}: {
	mipId: string;
	isArchived?: boolean;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: [QUERY_KEY, mipId, { isArchived }],
		queryFn: () => listTags({ isArchived }),
		select: (res) => res.payload ?? [],
		enabled,
	});
};

const errorMessage = (e: unknown, fallback: string) => {
	const detail = (e as { error?: { message?: string } } | undefined)?.error?.message;
	return detail ?? fallback;
};

export const useCreateTag = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: CreateTagDto) => createTag(body),
		onSuccess: () => {
			showToastV2({ state: 'success', message: 'Tag created' });
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({ state: 'error', message: errorMessage(e, 'Failed to create tag') });
		},
	});
};

export const useUpdateTag = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { tagId: string; body: UpdateTagDto }) => updateTag(args),
		onSuccess: () => {
			showToastV2({ state: 'success', message: 'Tag updated' });
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({ state: 'error', message: errorMessage(e, 'Failed to update tag') });
		},
	});
};

export const useDeleteTag = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { tagId: string }) => deleteTag(args),
		onSuccess: () => {
			showToastV2({ state: 'success', message: 'Tag deleted' });
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({ state: 'error', message: errorMessage(e, 'Failed to delete tag') });
		},
	});
};

export const useAttachClassifiersToTag = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { tagId: string; body: ClassifierAssignmentDto }) =>
			attachClassifiersToTag(args),
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message: errorMessage(e, 'Failed to add cohorts to tag'),
			});
		},
	});
};

export const useDetachClassifiersFromTag = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { tagId: string; body: ClassifierAssignmentDto }) =>
			detachClassifiersFromTag(args),
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message: errorMessage(e, 'Failed to remove cohorts from tag'),
			});
		},
	});
};
